import { JobClosingSurveyData, ValidationErrors, OutcomeType } from '../types/JobClosingSurvey';

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationErrors;
}

/**
 * Validates the job closing survey form data
 */
export function validateJobClosingSurvey(data: JobClosingSurveyData): ValidationResult {
  const errors: ValidationErrors = {};

  // Validate outcome selection
  if (!data.outcome) {
    errors.outcome = 'Please select an outcome for this job posting';
  }

  // Validate outcome_other if "other" is selected
  if (data.outcome === 'other') {
    if (!data.outcome_other || data.outcome_other.trim().length === 0) {
      errors.outcome_other = 'Please specify the outcome';
    } else if (data.outcome_other.trim().length < 3) {
      errors.outcome_other = 'Please provide more detail (minimum 3 characters)';
    } else if (data.outcome_other.trim().length > 500) {
      errors.outcome_other = 'Please keep the description under 500 characters';
    }
  }

  // Validate recommendation score (NPS) if provided
  if (data.recommendation_score !== undefined && data.recommendation_score !== null) {
    if (data.recommendation_score < 0 || data.recommendation_score > 10) {
      errors.general = 'Recommendation score must be between 0 and 10';
    }
  }

  // Validate additional comments length if provided
  if (data.additional_comments && data.additional_comments.trim().length > 1000) {
    errors.general = 'Additional comments must be under 1000 characters';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Validates a single field
 */
export function validateField(
  fieldName: keyof JobClosingSurveyData, 
  value: any, 
  formData: JobClosingSurveyData
): string | null {
  switch (fieldName) {
    case 'outcome':
      if (!value) {
        return 'Please select an outcome for this job posting';
      }
      return null;

    case 'outcome_other':
      if (formData.outcome === 'other') {
        if (!value || value.trim().length === 0) {
          return 'Please specify the outcome';
        }
        if (value.trim().length < 3) {
          return 'Please provide more detail (minimum 3 characters)';
        }
        if (value.trim().length > 500) {
          return 'Please keep the description under 500 characters';
        }
      }
      return null;

    case 'recommendation_score':
      if (value !== undefined && value !== null) {
        if (value < 0 || value > 10) {
          return 'Recommendation score must be between 0 and 10';
        }
      }
      return null;

    case 'additional_comments':
      if (value && value.trim().length > 1000) {
        return 'Additional comments must be under 1000 characters';
      }
      return null;

    default:
      return null;
  }
}

/**
 * Gets user-friendly outcome display text
 */
export function getOutcomeDisplayText(outcome: OutcomeType, outcomeOther?: string): string {
  switch (outcome) {
    case 'hired_through_platform':
      return 'We hired someone through your platform';
    case 'filled_another_source':
      return 'We filled the role through another source';
    case 'no_longer_hiring':
      return "We're no longer hiring for this position";
    case 'no_qualified_candidates':
      return "We didn't find qualified candidates";
    case 'still_hiring':
      return "We're still hiring but closing this posting";
    case 'other':
      return outcomeOther || 'Other';
    default:
      return 'Unknown outcome';
  }
}